import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Link, navigate } from 'gatsby'
import { useSetRecoilState } from 'recoil'
import { Col, Row, Stack } from 'react-bootstrap'
import debounce from 'lodash.debounce'
import { Button, RemoveButton } from '/src/components/common'
import { Loading } from '/src/components/loading'
import { cartInventoryState } from '/src/hooks/useCartInventory'
import { useCart, getCart, cartState } from '/src/hooks/useCart'
import { useUser, useCartInventory } from '/src/hooks'
import { getRequest, postRequest } from '/src/httpClient'
import { QuantityInputElement } from './elements'

import './cart_form.scss'

const CartFormLineItemsEmpty = () => {
  return <p>Cart is empty, please shop our <Link to="/products/all">product range.</Link></p>
}

const CartFormLineItemImage = ({ url }) => {
  if (!url) {
    return <></>
  }

  return (
    <img src={url} width="120px" height="120px" />
  )
}

const CartFormProductAttributes = ({ attributes }) => {
  return (
    <Row className="mt-1">
      {attributes.filter((v) => !v.label.includes('Size')).map(({ id, label, value }) => {
        return (
          <Col md={6} key={id} className="mb-1">
            <label>{label}:</label>
            <span>{value}</span>
          </Col>
        )
      })}
    </Row>
  )
}

const CartFormLineItem = ({
  id,
  cartId,
  attributes,
  register,
  setValue,
  inventory,
  setParentLoading
}) => {
  const { title, quantity, image, product_reference, total_price, variation_attributes } = attributes

  const [quantityValue, setQuantityValue] = useState(quantity)
  const [isRemoveLoading, setRemoveIsLoading] = useState(false)
  const [isQuantityLoading, setQuantityIsLoading] = useState(false)
  const setCart = useSetRecoilState(cartState)
  const setCartInventory = useSetRecoilState(cartInventoryState)

  const updateCartQuantity = useCallback(debounce((quantityValue) => {
    if (isQuantityLoading) return
    setQuantityIsLoading(true)
    setParentLoading(true)

    // Update cart quantity.
    postRequest('/api/cart/item/update', {
      id: id,
      cartId: cartId,
      data: {
        id: id,
        type: 'order-items--default',
        attributes: {
          quantity: quantityValue
        }
      }
    }).then(() => {
      getCart().then(result => {
        setCart(result)

        getRequest(`/api/inventory/cart?id=${cartId}`, new AbortController()).then(inventoryResult => {
          setCartInventory(inventoryResult)
          setParentLoading(false)
          setQuantityIsLoading(false)
        })
      })
    })
  }, 500), [])

  useEffect(() => {
    if (parseInt(quantityValue) === parseInt(quantity)) return
    updateCartQuantity(quantityValue)
  }, [quantityValue])

  const itemClasses = ['cart-form-line-item-container']
  if (!inventory) {
    itemClasses.push('no-stock')
  }

  const handleRemoveLineItem = () => {
    setParentLoading(true)
    setRemoveIsLoading(true)

    postRequest('/api/cart/delete', {
      id: id,
      cartId: cartId,
      type: 'order-item--default'
    }).then(() => {
      getCart().then(result => {
        setCart(result)

        getRequest(`/api/inventory/cart?id=${cartId}`, new AbortController()).then(inventoryResult => {
          setCartInventory(inventoryResult)
          setParentLoading(false)
          setRemoveIsLoading(false)
        })
      })
    })
  }

  return (
    <div className={itemClasses.join(' ')}>
      <Row>
        <Col lg={2} className="d-flex justify-content-center align-items-center">
          <CartFormLineItemImage url={image} />
        </Col>
        <Col lg={4}>
          <div className="product-title mb-1">
            <h4>
              {title}
            </h4>

            {!inventory && (
              <span>No stock</span>
            )}
          </div>
          <div className="product-attributes-container-top mb-3">
            <Row>
              <Col>
                <label>Reference: </label>
                <span>{product_reference}</span>
              </Col>
            </Row>
          </div>
          <div className="product-attributes-container">
            <CartFormProductAttributes attributes={variation_attributes} />
          </div>
        </Col>
        <Col lg={2}>
          <div className="product-price">
            <h4>{total_price.formatted}</h4>
          </div>
        </Col>
        <Col lg={2}>
          <Stack className="product-quantity d-flex justify-content-center" direction='horizontal'>
            {isQuantityLoading && (
              <Loading />
            )}
            <QuantityInputElement
              defaultValue={quantity} 
              elementName={`quantity_${id}`}
              register={register(`quantity_${id}`, {
                min: 1,
                required: true,
                disabled: isQuantityLoading,
                onChange: (event) => {
                  const newQuantity = parseInt(event.target.value)
                  if (newQuantity > 0) {
                    setValue('quantity', newQuantity)
                    setQuantityValue(newQuantity)
                  }
                }
              })}
              quantity={quantityValue}
              setQuantity={setQuantityValue}
              setValue={setValue}
              isDisabled={isQuantityLoading} />
          </Stack>
        </Col>
        <Col lg={2}>
          <div className="d-flex justify-content-end">
            <RemoveButton disabled={isRemoveLoading} onClick={() => handleRemoveLineItem()}>
              Remove
              {isRemoveLoading && (
                <Loading />
              )}
            </RemoveButton>
          </div>
        </Col>
      </Row>
    </div>
  )
}

const CartFormLineItems = ({ cartId, items, inventory, register, setValue, setParentLoading }) => {
  const hasInventory = ({ attributes }) => {
    const { sku } = attributes
    return inventory.results ? inventory.results[sku] : false
  }

  if (items && items.length === 0 || !items) {
    return <CartFormLineItemsEmpty />
  }

  return (
    <div className="cart-form-line-items-container">
      {items.map((item, index) => 
        <CartFormLineItem 
          key={item.id} 
          register={register} 
          setValue={setValue}
          cartId={cartId}
          inventory={hasInventory(item)}
          setParentLoading={setParentLoading}
          {...item}
        />
      )}
    </div>
  )
}

const CartFormCarts = ({ register, setValue, cart }) => {
  const useAuth = useUser()
  const inventory = useCartInventory(cart ? cart.id : null)
  const [isLoading, setIsLoading] = useState(false)

  const orderItems = useMemo(() => {
    if (!cart) return null
    return cart.relationships.order_items.data
  }, [cart])

  const hasInventory = useMemo(() => {
    if (!inventory.results) return null 
    return Object.values(inventory.results).every((value) => value)
  }, [inventory.results])

  const canCheckout = useMemo(() => {
    return !isLoading && hasInventory
  })

  if (cart && orderItems && orderItems.length === 0) {
    return <CartFormLineItemsEmpty />
  }

  return (
    <Row>
      {useAuth === false && (
        <Col xs={12} className="mb-4">
          <p>Please <Link to="/user/login">login</Link> or <Link to="/user/register">register</Link> before contining to checkout.</p>
        </Col>
      )}
      {hasInventory === false && (
        <Col xs={12} className="mb-4">
          <p>Your cart has items which are currently not in stock at your selected store. Please remove these items or change store to continue checkout.</p>
        </Col>
      )}
      <Col xs={12} className="mb-4">
        <CartFormLineItems
          register={register}
          setValue={setValue}
          cartId={cart.id}
          items={orderItems}
          setParentLoading={setIsLoading}
          inventory={inventory} />
      </Col>
      <Col xs={12}>
        <div className="d-flex justify-content-end">
          <Stack gap={3} direction="horizontal" className="form-actions">
            <Link className="button outline" to="/products/all">Continue Shopping</Link>
            {useAuth ? (
              <>
                <Button 
                  disabled={canCheckout ? false : true}
                  variant="primary" 
                  type="submit">
                  {hasInventory === true && (
                    <>Checkout</>
                  )}
                  {hasInventory === false && (
                    <>Not all stock available</>
                  )}
                  {hasInventory === null && (
                    <>Checking...</>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Link className="button outline" to="/user/login">Login</Link>
                <Link className="button outline" to="/user/register">Sign Up</Link>
              </>
            )}
          </Stack>
        </div>
      </Col>
    </Row>
  )
}

export const CartForm = () => {
  return <CartFormElements />
}

export const CartFormElements = () => {
  const { register, setValue, handleSubmit } = useForm()
  const cart = useCart()

  const onSubmit = (data) => {
    if (!cart) return
    // Validate inventory.
    // @todo, ensure field_store is used.
    getRequest(`/api/inventory/cart?id=${cart.id}`, new AbortController()).then(value => {
      if (value) {
        // Track start of commerce checkout.
        window.dataLayer = window.dataLayer || []
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'checkoutPaneCartSubmit',
            order: {
              id: cart.id,
              number: cart.attributes.order_number,
              total: cart.attributes.order_total.total.number,
              user: cart.attributes.email,
              order_items: cart.relationships.order_items.data.map((item) => {
                return {
                  id: item.id,
                  ref: item.attributes.product_reference,
                  title: item.attributes.title,
                  quantity: item.attributes.quantity,
                  total_price: item.attributes.total_price.number,
                  unit_price: item.attributes.unit_price.number,
                  attributes: item.attributes.variation_attributes?.map((attribute) => {
                    return {
                      label: attribute.label,
                      value: attribute.value
                    }
                  })
                }
              })
            },
            store: {
              name: cart.store.name,
              plant_id: cart.store.dsap_plant_id
            }
          })
        }

        navigate('/checkout/shipping')
      }
    })
  }

  return (
    <div className="cart-form-container">
      <form id="cart-form" onSubmit={handleSubmit(onSubmit)}>
        {cart !== null && (
          <CartFormCarts register={register} setValue={setValue} cart={cart} /> 
        )}
        {cart === null && (
          <Loading />
        )}
      </form>
    </div>
  )
}
